// Persistence for the Web SDK v5 renewable login tokens.
//
// The bootstrap is a one-time-key login (password stays on
// the server). It returns `loginTokens` — an access token
// (~1 month) + a refresh token. We persist them per
// username so a returning agent skips the OTK round-trip
// and logs straight in with `loginAccessToken`, refreshing
// before expiry. Tokens are NOT secrets in the
// props-are-public sense — they come from the SDK, never
// from Inertia props, and are scoped to this browser via
// the `deviceToken`.

const STORAGE_PREFIX = "v2.voice.tokens."

export interface StoredTokens {
  accessToken: string
  // Absolute ms-epoch expiry (computed from the SDK's
  // relative `accessExpire` seconds at persist time).
  accessExpiresAt: number
  refreshToken: string
  refreshExpiresAt: number
}

// The shape the SDK returns from loginOneTimeKey /
// refreshTokens (`Core.LoginTokens`).
export interface SdkLoginTokens {
  accessToken: string
  accessExpire: number
  refreshToken: string
  refreshExpire: number
}

function keyFor(username: string): string {
  return `${STORAGE_PREFIX}${username}`
}

export function fromSdkTokens(
  tokens: SdkLoginTokens,
  now: number = Date.now(),
): StoredTokens {
  return {
    accessToken: tokens.accessToken,
    accessExpiresAt: now + tokens.accessExpire * 1000,
    refreshToken: tokens.refreshToken,
    refreshExpiresAt: now + tokens.refreshExpire * 1000,
  }
}

export function loadTokens(username: string): StoredTokens | null {
  try {
    const raw = window.localStorage.getItem(keyFor(username))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredTokens>
    if (
      typeof parsed.accessToken !== "string" ||
      typeof parsed.accessExpiresAt !== "number" ||
      typeof parsed.refreshToken !== "string" ||
      typeof parsed.refreshExpiresAt !== "number"
    ) {
      return null
    }
    return parsed as StoredTokens
  } catch {
    return null
  }
}

export function saveTokens(
  username: string,
  tokens: StoredTokens,
): void {
  try {
    window.localStorage.setItem(
      keyFor(username),
      JSON.stringify(tokens),
    )
  } catch {
    // Best-effort: a failed persist just means the next
    // load falls back to the OTK bootstrap.
  }
}

export function clearTokens(username: string): void {
  try {
    window.localStorage.removeItem(keyFor(username))
  } catch {
    // ignore
  }
}

// A small skew keeps us from racing the server clock: treat
// a token as expired a minute before its real expiry.
const EXPIRY_SKEW_MS = 60_000

export function accessTokenUsable(
  tokens: StoredTokens,
  now: number = Date.now(),
): boolean {
  return tokens.accessExpiresAt - EXPIRY_SKEW_MS > now
}

export function refreshTokenUsable(
  tokens: StoredTokens,
  now: number = Date.now(),
): boolean {
  return tokens.refreshExpiresAt - EXPIRY_SKEW_MS > now
}
